'use client'

import React, { useState, useEffect, useRef } from 'react'
import LandingHero from '@/components/landing/LandingHero'
import ExploreCategories from '@/components/landing/ExploreCategories'
import HowItWorksToggle from '@/components/landing/HowItWorksToggle'
import BigPromoCard from '@/components/landing/BigPromoCard'
import PricingCards from '@/components/landing/PricingCards'
import TrustAndTestimonials from '@/components/landing/TrustAndTestimonials'
import FAQ from '@/components/landing/FAQ'
import BottomCTA from '@/components/landing/BottomCTA'
import { LandingEventName, trackLandingEvent } from '@/lib/landing-analytics'

const LandingPage = () => {
  const currentYear = new Date().getFullYear()
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set())
  const sectionRefs = useRef<(HTMLElement | null)[]>([])

  const buildLoginRedirect = (target: string) => `/auth?redirect=${encodeURIComponent(target)}`

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const handleFaqKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleFaq(index)
      return
    }

    if (e.key === 'Escape' && openFaq === index) {
      e.preventDefault()
      setOpenFaq(null)
    }
  }

  // Intersection Observer로 스크롤 애니메이션 처리
  useEffect(() => {
    trackLandingEvent('view_landing')
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    sectionRefs.current.forEach((section, index) => {
      if (!section) return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => new Set(prev).add(index))
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px',
        }
      )

      observer.observe(section)
      observers.push(observer)
    })

    return () => {
      observers.forEach((observer) => observer.disconnect())
    }
  }, [])

  const handleLandingClickCapture = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement | null
    const eventElement = target?.closest('[data-landing-event]') as HTMLElement | null
    if (!eventElement) return

    const eventName = eventElement.dataset.landingEvent as LandingEventName | undefined
    if (!eventName) return

    trackLandingEvent(eventName)

    if (
      eventName === 'click_cta_hero_primary' ||
      eventName === 'click_cta_promo_primary' ||
      eventName === 'click_cta_bottom_primary'
    ) {
      trackLandingEvent('onboarding_start', { source: eventName })
    }
  }

  const faqs = [
    {
      question: '무료로 사용할 수 있는 범위는 어디까지인가요?',
      summary: '프로필 등록, 프로젝트 추천 확인, 제안서 작성/제출은 무료입니다.',
      answer: '프리랜서 기준으로 가입, 프로필 등록, 키워드 등록, 추천 요청 확인, 제안서 작성 및 제출까지 무료로 이용할 수 있습니다.',
    },
    {
      question: '팀으로도 참여할 수 있나요?',
      summary: '개인과 팀 모두 가능하며 프로젝트마다 방식 선택이 가능합니다.',
      answer: '네, 개인 또는 팀으로 참여할 수 있습니다. 팀 프로필을 구성해 역할을 나누고, 프로젝트 성격에 맞춰 제안 방식을 선택하면 됩니다.',
    },
    {
      question: '제안서는 어떤 방식으로 작성하나요?',
      summary: '가격·기간·산출물·범위를 한 장 구조로 정리해 제출합니다.',
      answer: '제안서는 핵심 항목(가격, 기간, 산출물, 범위)을 구조화된 템플릿으로 작성합니다. 초안 저장 후 수정/재제출도 쉽게 진행할 수 있습니다.',
    },
    {
      question: '추천/노출 기준은 어떻게 되나요?',
      summary: '키워드 적합도, 프로필 완성도, 응답 성실도를 함께 반영합니다.',
      answer: '요청 내용과의 키워드 적합도, 포트폴리오/프로필 완성도, 응답 속도 및 협의 이력 등을 기준으로 추천과 노출 우선순위가 결정됩니다.',
    },
    {
      question: '알림은 어떤 채널로 받을 수 있나요?',
      summary: '서비스 내 알림을 기본으로 제공하며 주요 상태 변경을 즉시 확인할 수 있습니다.',
      answer: '기본적으로 서비스 내 알림으로 요청 도착, 협의 업데이트, 채택 여부 등 핵심 이벤트를 받아볼 수 있습니다. 채널 옵션은 서비스 정책에 따라 순차적으로 확장됩니다.',
    },
  ]

  return (
    <div className="min-h-screen w-full bg-white">
      <main className="flex w-full flex-col" onClickCapture={handleLandingClickCapture}>
        <LandingHero buildLoginRedirect={buildLoginRedirect} />

        {/* HowItWorksToggle */}
        <div
          ref={(el) => {
            sectionRefs.current[0] = el
          }}
        >
          <HowItWorksToggle isVisible={visibleSections.has(0)} />
        </div>

        {/* BigPromoCard */}
        <div
          ref={(el) => {
            sectionRefs.current[1] = el
          }}
        >
          <BigPromoCard buildLoginRedirect={buildLoginRedirect} isVisible={visibleSections.has(1)} />
        </div>

        {/* PricingCards */}
        <div
          ref={(el) => {
            sectionRefs.current[2] = el
          }}
        >
          <PricingCards buildLoginRedirect={buildLoginRedirect} isVisible={visibleSections.has(2)} />
        </div>

        {/* TrustAndTestimonials */}
        <div
          ref={(el) => {
            sectionRefs.current[3] = el
          }}
        >
          <TrustAndTestimonials isVisible={visibleSections.has(3)} />
        </div>

        {/* FAQ */}
        <div
          ref={(el) => {
            sectionRefs.current[4] = el
          }}
        >
          <FAQ
            faqs={faqs}
            openFaq={openFaq}
            toggleFaq={toggleFaq}
            handleFaqKeyDown={handleFaqKeyDown}
            isVisible={visibleSections.has(4)}
          />
        </div>

        {/* ExploreCategories - 선택 탐색은 후순위 배치 */}
        <div
          ref={(el) => {
            sectionRefs.current[5] = el
          }}
        >
          <ExploreCategories isVisible={visibleSections.has(5)} />
        </div>

        {/* BottomCTA - 유지 */}
        <BottomCTA buildLoginRedirect={buildLoginRedirect} />
      </main>

      {/* Footer */}
      <footer className="w-full bg-gradient-to-b from-gray-900 to-gray-800 px-4 py-12 text-white sm:px-6 md:py-16 lg:px-8">
        <div className="mx-auto max-w-7xl text-center">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600">
            <span className="text-2xl font-bold">L</span>
          </div>
          <div className="text-2xl font-bold md:text-3xl">LINKUS</div>
          <p className="mt-4 text-sm text-gray-400 md:text-base">
            &copy; {currentYear} LINKUS. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
